import type { ChangeEvent } from "react"
import { UnlinkIcon } from "components/ui/icons/unlink/UnlinkIcon"
import { ArrowUpRightIcon } from "components/ui/icons/arrowUpRight/ArrowUpRightIcon"
import style from "./ElevationReferenceRow.module.scss"

export type TElevationRowState = "default" | "filled" | "connected" | "error"

type Props = {
	elevationNumber: string
	connectedPageName?: string
	referenceLabel: string
	state: TElevationRowState
	connectPending?: boolean
	onElevationNumberChange: (next: string) => void
	onReferenceLabelChange: (next: string) => void
	onElevationNumberBlur?: () => void
	onReferenceLabelBlur?: () => void
	onRemove: () => void
	onConnect: () => void
	onUnlink: () => void
	onNavigateToEref?: () => void
}

const RemoveIcon = () => (
	<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M2 2L10 10M2 10L10 2" stroke="#000" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
)

const TriangleAlertIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 6V9M8 11H8.005M6.86 2.57L1.79 11.0001C1.6132 11.3068 1.51943 11.6544 1.5184 12.0085C1.51738 12.3626 1.60914 12.7107 1.78416 13.0184C1.95919 13.326 2.21134 13.5825 2.51578 13.7625C2.82022 13.9425 3.16637 14.0398 3.52 14.0445H13.66C14.0136 14.0398 14.3598 13.9425 14.6642 13.7625C14.9687 13.5825 15.2208 13.326 15.3958 13.0184C15.5709 12.7107 15.6626 12.3626 15.6616 12.0085C15.6606 11.6544 15.5668 11.3068 15.39 11.0001L10.32 2.57C10.1444 2.27148 9.89274 2.02351 9.59042 1.85077C9.2881 1.67802 8.94634 1.58655 8.598 1.58655C8.24966 1.58655 7.9079 1.67802 7.60558 1.85077C7.30326 2.02351 7.05162 2.27148 6.876 2.57H6.86Z"
			stroke="#EC404C"
			strokeWidth="1.4"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

export const ElevationReferenceRow = ({
	elevationNumber,
	connectedPageName,
	referenceLabel,
	state,
	connectPending = false,
	onElevationNumberChange,
	onReferenceLabelChange,
	onElevationNumberBlur,
	onReferenceLabelBlur,
	onRemove,
	onConnect,
	onUnlink,
	onNavigateToEref,
}: Props) => {
	const handleElevationChange = (e: ChangeEvent<HTMLInputElement>) => onElevationNumberChange(e.target.value)
	const handleLabelChange = (e: ChangeEvent<HTMLInputElement>) => onReferenceLabelChange(e.target.value)

	const renderConnectionSlot = () => {
		if (state === "connected") {
			return (
				<>
					<button
						type="button"
						className={`${style.connectedLabel} ${onNavigateToEref ? style.connectedLabelInteractive : ""}`}
						onClick={onNavigateToEref}
						disabled={!onNavigateToEref}
						aria-label="Navigate to elevation"
					>
						<span className={style.dot} />
						<span className={style.connectedLabelText}>
							{connectedPageName ? `${elevationNumber} – ${connectedPageName}` : elevationNumber}
						</span>
						<span className={style.connectedLabelArrow} aria-hidden="true">
							<ArrowUpRightIcon />
						</span>
					</button>
					<button type="button" className={style.iconButton} onClick={onUnlink} aria-label="Unlink elevation">
						<UnlinkIcon stroke="#000" />
					</button>
				</>
			)
		}

		if (state === "error") {
			return (
				<>
					<button
						type="button"
						className={`${style.connectButton} ${style.connectButtonActive}`}
						onClick={onConnect}
						disabled={connectPending}
					>
						{connectPending ? "Connecting…" : "Retry"}
					</button>
					<span className={style.slot}>
						<TriangleAlertIcon />
					</span>
				</>
			)
		}

		const disabled = state === "default" || connectPending
		return (
			<>
				<button
					type="button"
					className={`${style.connectButton} ${disabled ? style.connectButtonDisabled : style.connectButtonActive}`}
					onClick={onConnect}
					disabled={disabled}
				>
					{connectPending ? "Connecting…" : "Search and connect"}
				</button>
				<span className={style.slot} aria-hidden="true" />
			</>
		)
	}

	return (
		<div className={style.row}>
			<div className={style.field}>
				<span className={style.label}>Elevation number</span>
				<div className={style.control}>
					<input
						className={style.input}
						value={elevationNumber}
						onChange={handleElevationChange}
						onBlur={onElevationNumberBlur}
						placeholder=""
					/>
					<button type="button" className={style.iconButton} onClick={onRemove} aria-label="Remove elevation">
						<RemoveIcon />
					</button>
				</div>
			</div>

			<div className={style.field}>
				<span className={style.label}>Reference label</span>
				<div className={style.control}>
					<input
						className={style.input}
						value={referenceLabel}
						onChange={handleLabelChange}
						onBlur={onReferenceLabelBlur}
						placeholder=""
					/>
					<span className={style.slot} aria-hidden="true" />
				</div>
			</div>

			<div className={style.field}>
				<span className={style.label}>Elevation</span>
				<div className={style.control}>{renderConnectionSlot()}</div>
			</div>
		</div>
	)
}
